import { Cloud, Menu as MenuIcon, MenuOpen, Search } from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  TooltipProps,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';

import { LeftNav } from './left-nav';
import { SiteHeaderNavLink } from './site-header';
import { useMuiSwitchableTheme } from '../../theme';
import { LogoText } from '../../assets/logo-text';

export const EnvironmentAlert: React.FC<{
  env?: string;
  collapsed?: boolean;
  sx?: TooltipProps['sx'];
}> = ({ env = '', collapsed = false, sx = {} }) => {
  if (!env || env === 'production') return null;

  return (
    <Tooltip
      title={`Current Environment: ${env}`}
      sx={{ textTransform: 'capitalize' }}
    >
      <Chip
        size="small"
        label={!collapsed ? env : null}
        color="info"
        icon={<Cloud />}
        sx={{ m: 2, textTransform: 'capitalize', p: 1, ...sx }}
        slotProps={{ label: { sx: { pr: collapsed ? 0 : 1 } } }}
      />
    </Tooltip>
  );
};

const NavLink = ({
  href,
  label,
  icon,
  dropDownItems,
  onClick,
}: SiteHeaderNavLink) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const { themeMode } = useMuiSwitchableTheme();

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!dropDownItems?.length) {
    return (
      <Button
        component={Link}
        sx={{
          m: 1,
          color: 'inherit',
          fontWeight: 'normal',
          fontSize: { xs: '0.7rem', lg: '0.875rem' },
        }}
        startIcon={icon}
        to={href ?? ''}
        onClick={onClick}
      >
        {label}
      </Button>
    );
  }

  return (
    <Box
      onMouseLeave={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        '&:hover': { pointer: 'cursor' },
      }}
    >
      <Button
        sx={{
          m: 1,
          color: 'inherit',
          fontSize: { xs: '0.7rem', lg: '0.875rem' },
        }}
        startIcon={icon}
        onMouseEnter={handleOpen}
        onClick={handleOpen}
      >
        {label}
      </Button>
      <Menu
        disablePortal
        hideBackdrop
        sx={{ mt: 6 }}
        slotProps={{
          paper: {
            sx: {
              borderTopRightRadius: 0,
              borderTopLeftRadius: 0,
              backgroundColor: 'primary.main',
              filter:
                themeMode === 'light' ? 'brightness(1.05)' : 'brightness(0.95)',
              color: 'primary.contrastText',
              boxShadow: theme.shadows[4],
            },
          },
        }}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          onMouseLeave: handleClose,
        }}
      >
        {dropDownItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              item.onClick?.();
              handleClose();
            }}
            {...(item.href && {
              component: Link,
              to: item.href,
              target: item.external ? '_blank' : '_self',
            })}
            sx={{
              m: 1,
              color: 'success.contrastText',
              fontWeight: 'normal',
              '&:hover': { fontWeight: 'bold', pointer: 'cursor' },
            }}
          >
            <Typography sx={{ textAlign: 'center' }}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export const HeaderAppBar = ({
  links,
  authComponent = null,
  baseColor = 'success',
  env = '',
  globalSearchElement,
}: {
  links: SiteHeaderNavLink[];
  authComponent?: React.ReactNode;
  persistentLeftNav?: boolean;
  baseColor?:
    | 'success'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'error';
  env?: string;
  globalSearchElement?: React.ReactNode | null;
}) => {
  const { navMode, navCollapsed, setNavCollapsed, themeMode } =
    useMuiSwitchableTheme();

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: `${baseColor}.${
            themeMode === 'light' ? 'main' : 'dark'
          }`,
          color: `${baseColor}.contrastText`,
          display: navMode === 'top' ? 'flex' : 'none',
          height: 64,
          backgroundImage: 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ alignItems: 'center', height: 64 }}>
            <LogoText
              sx={{
                display: { xs: 'none', md: 'flex' },
                height: 30,
                minWidth: 120,
                maxWidth: 120,
                mr: 1,
                color: `${baseColor}.contrastText`,
              }}
            />
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                height: 32,
              }}
            >
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={() => setNavCollapsed(!navCollapsed)}
                color="inherit"
              >
                {navCollapsed ? <MenuIcon /> : <MenuOpen />}
              </IconButton>
            </Box>
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Link
                to="/processor/calendar"
                style={{
                  display: 'flex',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <LogoText
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    maxWidth: 120,
                    color: 'inherit',
                    height: 32,
                  }}
                />
              </Link>
            </Box>
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                width: 56,
              }}
            />
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {links.map((page) => (
                <NavLink {...page} key={page.label} />
              ))}
            </Box>
            <EnvironmentAlert
              env={env}
              collapsed
              sx={{ m: 0, mx: 1, height: 32 }}
            />
            <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
              {authComponent}
            </Box>
            {globalSearchElement}
          </Toolbar>
        </Container>
      </AppBar>
      <LeftNav
        items={links}
        authComponent={authComponent}
        baseColor={baseColor}
        handleDrawerClose={() => navMode === 'top' && setNavCollapsed(true)}
      />
    </>
  );
};
